import {
  SDI_RATES,
  SDI_RATE_LABELS,
  formatByKey,
  rateForFormat,
  shortfall,
  type SdiRate,
} from '@routr/shared'

/**
 * Can this gear actually run the show?
 *
 * A show format implies a data rate; a device has a fastest rate it handles.
 * Where the second is lower than the first, that box cannot do this show, and
 * finding that out on site is the expensive way.
 *
 * The rule, as everywhere else in this app: a device with no researched figure
 * is left OUT of the check, never assumed capable. An unknown is reported as
 * an unknown. Quietly passing every unresearched box would turn a green screen
 * into a promise nobody made.
 */

/** Connectors a picture can arrive on. */
export const SDI_CONNECTORS = ['BNC (SDI)', 'HD-BNC', 'Mini-BNC', 'Micro BNC', 'Mini DIN 1.0/2.3']

/**
 * A BNC is not automatically a picture. Reference, genlock, black burst, word
 * clock, timecode, MADI and analogue component all run down the same coax on
 * the same connector, and none of them have an opinion about the show format.
 *
 * A Canon EOS C70 has no SDI whatsoever — its only BNC is a timecode input —
 * so counting it as SDI gear puts it on screen as a box nobody has researched,
 * which buries the unknowns that are real.
 */
const SYNC_OR_ANALOGUE =
  /^(ref\b|reference|genlock|black ?burst|word ?clock|time ?code|ltc\b|viewfinder|component|y\/|c\/|[ypr]b?r?\s+(in|out))/i

/** ...unless the same connector is also named as a video output. */
const NAMED_AS_VIDEO = /\bvideo\s*out/i

/**
 * Does this port actually carry SDI video?
 *
 * The caller of `checkCapability` uses this to decide which gear on a canvas
 * is worth judging at all.
 */
export function carriesSdi(port: { name: string; connector: string; signal: string }): boolean {
  if (!SDI_CONNECTORS.includes(port.connector)) return false
  if (port.signal !== 'VIDEO') return false
  return !SYNC_OR_ANALOGUE.test(port.name) || NAMED_AS_VIDEO.test(port.name)
}

export type CapabilityDevice = {
  id: string
  label: string
  /** Fastest SDI rate the maker claims, or null if nobody has looked it up. */
  maxSdiRate: string | null
  maxFormat: string | null
  capabilityUrl: string | null
}

export type CapabilityVerdict =
  | { kind: 'OK'; rate: SdiRate }
  | { kind: 'TOO_SLOW'; rate: SdiRate; deviceRate: SdiRate; label: string }
  | { kind: 'UNKNOWN'; label: string }
  | { kind: 'NO_FORMAT' }

const ORDER: Record<SdiRate, number> = Object.fromEntries(
  SDI_RATES.map((rate, index) => [rate, index]),
) as Record<SdiRate, number>

export function isSdiRate(value: string | null | undefined): value is SdiRate {
  return value != null && (SDI_RATES as readonly string[]).includes(value)
}

/** One device against one show format. */
export function checkDevice(device: CapabilityDevice, showFormat: string | null): CapabilityVerdict {
  const rate = rateForFormat(showFormat)
  if (rate == null) return { kind: 'NO_FORMAT' }
  if (!isSdiRate(device.maxSdiRate)) return { kind: 'UNKNOWN', label: device.label }
  if (ORDER[device.maxSdiRate] < ORDER[rate]) {
    return { kind: 'TOO_SLOW', rate, deviceRate: device.maxSdiRate, label: device.label }
  }
  return { kind: 'OK', rate }
}

export type CapabilityReport = {
  /** Devices that cannot carry the show format. The ones that stop a job. */
  tooSlow: { label: string; deviceRate: SdiRate; rateLabel: string; deviceRateLabel: string }[]
  /** Devices nobody has researched. Counted and named, never assumed fine. */
  unknown: string[]
  checked: number
  rate: SdiRate | null
}

/**
 * Every device on a canvas against the show format.
 *
 * Only devices that actually carry SDI are worth checking — a rack PDU has no
 * opinion about 2160p50 — so the caller passes the ones with SDI ports.
 */
export function checkCapability(
  devices: CapabilityDevice[],
  showFormat: string | null,
): CapabilityReport {
  const rate = rateForFormat(showFormat)
  const report: CapabilityReport = { tooSlow: [], unknown: [], checked: 0, rate }
  if (rate == null) return report

  const seenSlow = new Set<string>()
  const seenUnknown = new Set<string>()

  for (const device of devices) {
    const verdict = checkDevice(device, showFormat)
    if (verdict.kind === 'TOO_SLOW' && !seenSlow.has(verdict.label)) {
      seenSlow.add(verdict.label)
      report.tooSlow.push({
        label: verdict.label,
        deviceRate: verdict.deviceRate,
        rateLabel: SDI_RATE_LABELS[verdict.rate],
        deviceRateLabel: SDI_RATE_LABELS[verdict.deviceRate],
      })
    } else if (verdict.kind === 'UNKNOWN' && !seenUnknown.has(verdict.label)) {
      seenUnknown.add(verdict.label)
      report.unknown.push(verdict.label)
    }
    if (verdict.kind === 'OK' || verdict.kind === 'TOO_SLOW') report.checked += 1
  }
  return report
}

/* ------------------------------------------------------------------ *
 * The link check.
 *
 * The check above asks one question of the whole project: can this gear run
 * the show format? Right for broadcast, wrong for everyone else. A podcaster
 * never sets a show format, and a 4K camera into a 1080p capture card is a
 * deliberate rig rather than a mistake.
 *
 * What goes wrong at that tier is a single link that quietly degrades, and it
 * is answerable per run without any project setting at all — which is why this
 * needs no control and works in Simple mode as pure consequence.
 * ------------------------------------------------------------------ */

export type LinkDevice = {
  label: string
  /** Top format it emits. */
  maxOutFormat: string | null
  /** Top format its inputs accept. */
  maxInFormat: string | null
  /** Top format that reaches the host or the recording. */
  maxCaptureFormat: string | null
}

export type LinkRun = {
  id: string
  label: string
  source: LinkDevice
  target: LinkDevice
}

export type LinkFinding = {
  runId: string
  label: string
  /**
   * INPUT — the far end will not accept what this end sends, so it may not
   * lock at all. CAPTURE — it accepts the picture and then records less of it,
   * which is the one nobody notices until afterwards.
   */
  kind: 'INPUT' | 'CAPTURE'
  /** Labels, ready to put in a sentence. */
  sourceFormat: string
  sinkFormat: string
  resolution: boolean
  frameRate: boolean
}

/**
 * Runs whose far end cannot carry everything the near end sends.
 *
 * A device with no published figure is left OUT, never assumed capable — the
 * same rule as the SDI check, for the same reason.
 */
export function checkLinks(runs: LinkRun[]): LinkFinding[] {
  const findings: LinkFinding[] = []
  for (const run of runs) {
    const out = formatByKey(run.source.maxOutFormat)
    if (out == null) continue

    // A link that will not lock beats a link that merely records less, so the
    // input ceiling is asked first. One finding per run: two lines about one
    // cable is noise, and the first one already tells the crew to convert.
    const ceilings = [
      { kind: 'INPUT' as const, sink: formatByKey(run.target.maxInFormat) },
      { kind: 'CAPTURE' as const, sink: formatByKey(run.target.maxCaptureFormat) },
    ]
    for (const { kind, sink } of ceilings) {
      if (sink == null) continue
      const gap = shortfall(out, sink)
      if (gap == null) continue
      findings.push({
        runId: run.id,
        label: run.label,
        kind,
        sourceFormat: out.label,
        sinkFormat: sink.label,
        ...gap,
      })
      break
    }
  }
  return findings
}

/** One sentence a crew can act on. */
export function describeLink(finding: LinkFinding): string {
  const loses = finding.resolution && finding.frameRate ? 'resolution and frame rate'
    : finding.resolution ? 'resolution'
    : 'frame rate'
  const verb = finding.kind === 'CAPTURE' ? 'records at' : 'only accepts'
  return `${finding.label}: the source puts out ${finding.sourceFormat}, `
    + `but the far end ${verb} ${finding.sinkFormat} — you lose ${loses}.`
}
